'use client';

import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

export const GSPLAT_FORMATS = ['ply', 'splat', 'spz', 'ksplat', 'sog'] as const;

export function isSplatAssetFormat(format?: string | null): boolean {
  return !!format && GSPLAT_FORMATS.includes(format.toLowerCase() as (typeof GSPLAT_FORMATS)[number]);
}

export function isSplatAssetUrl(url?: string | null): boolean {
  if (!url) return false;
  const base = url.split('?')[0]?.toLowerCase() ?? '';
  return /\.(ply|splat|spz|ksplat|sog|view\.splat)$/.test(base);
}

export function toViewSplatUrl(url: string): string {
  const [base, query] = url.split('?');
  if (base.toLowerCase().endsWith('.view.splat')) return url;
  const splatBase = base.replace(/\.(ply|sog|spz|ksplat|splat)$/i, '.view.splat');
  return query ? `${splatBase}?${query}` : splatBase;
}

export interface ParsedSplat {
  centerAndScaleData: Float32Array;
  covAndColorData: Uint32Array;
  matrices: Float32Array;
  numVertices: number;
}

interface CloudEntry {
  parsed: ParsedSplat;
  worldMatrix: THREE.Matrix4;
  dirty: boolean;
}

export function parseSplatBuffer(buffer: ArrayBuffer): ParsedSplat {
  const rowLen = 32;
  const numVertices = Math.floor(buffer.byteLength / rowLen);
  const centerAndScaleData = new Float32Array(numVertices * 4);
  const covAndColorData = new Uint32Array(numVertices * 4);
  const covAndColorDataUint8 = new Uint8Array(covAndColorData.buffer);
  const covAndColorDataInt16 = new Int16Array(covAndColorData.buffer);
  const matrices = new Float32Array(numVertices * 16);
  const uBuf = new Uint8Array(buffer);
  const fBuf = new Float32Array(buffer);
  const covIndexes = [0, 1, 2, 5, 6, 10];

  for (let i = 0; i < numVertices; i++) {
    const quat = new THREE.Quaternion(
      -(uBuf[32 * i + 29] - 128) / 128,
      (uBuf[32 * i + 30] - 128) / 128,
      (uBuf[32 * i + 31] - 128) / 128,
      -(uBuf[32 * i + 28] - 128) / 128,
    );
    quat.invert();

    const center = new THREE.Vector3(fBuf[8 * i], fBuf[8 * i + 1], -fBuf[8 * i + 2]);
    const scale = new THREE.Vector3(fBuf[8 * i + 3], fBuf[8 * i + 4], fBuf[8 * i + 5]);
    const mtx = new THREE.Matrix4();
    mtx.makeRotationFromQuaternion(quat);
    mtx.transpose();
    mtx.scale(scale);
    const mtxT = mtx.clone();
    mtx.transpose();
    mtx.premultiply(mtxT);
    mtx.setPosition(center);

    let maxVal = 0;
    for (const j of covIndexes) maxVal = Math.max(maxVal, Math.abs(mtx.elements[j]));
    maxVal = maxVal || 1;

    let dst = i * 4;
    centerAndScaleData[dst] = center.x;
    centerAndScaleData[dst + 1] = -center.y;
    centerAndScaleData[dst + 2] = center.z;
    centerAndScaleData[dst + 3] = maxVal / 32767;

    dst = i * 8;
    for (let j = 0; j < covIndexes.length; j++) {
      covAndColorDataInt16[dst + j] = (mtx.elements[covIndexes[j]] * 32767) / maxVal;
    }

    dst = (i * 4 + 3) * 4;
    covAndColorDataUint8[dst] = uBuf[32 * i + 24];
    covAndColorDataUint8[dst + 1] = uBuf[32 * i + 25];
    covAndColorDataUint8[dst + 2] = uBuf[32 * i + 26];
    covAndColorDataUint8[dst + 3] = uBuf[32 * i + 27];

    mtx.elements[15] = (Math.max(scale.x, scale.y, scale.z) * uBuf[32 * i + 27]) / 255;
    for (let j = 0; j < 16; j++) matrices[i * 16 + j] = mtx.elements[j];
  }

  return { centerAndScaleData, covAndColorData, matrices, numVertices };
}

const workerSource = `
self.onmessage = function(e) {
  if (e.data.method === 'push') {
    self._matrices = new Float32Array(e.data.matrices);
  } else if (e.data.method === 'sort') {
    if (!self._matrices) { self.postMessage({ indices: new Uint32Array(0) }, []); return; }
    var matrices = self._matrices, view = new Float32Array(e.data.view);
    var vertexCount = matrices.length / 16;
    var maxD = -Infinity, minD = Infinity, valid = 0;
    var depthList = new Float32Array(vertexCount);
    var sizeList = new Int32Array(depthList.buffer);
    var validIdx = new Int32Array(vertexCount);
    for (var i = 0; i < vertexCount; i++) {
      var depth = view[0]*matrices[i*16+12] + view[1]*matrices[i*16+13] + view[2]*matrices[i*16+14] + view[3];
      if (depth < 0 && matrices[i*16+15] > 0.0001 * depth) {
        depthList[valid] = depth; validIdx[valid] = i; valid++;
        if (depth > maxD) maxD = depth;
        if (depth < minD) minD = depth;
      }
    }
    var inv = maxD === minD ? 1 : 65535 / (maxD - minD);
    var counts = new Uint32Array(65536);
    for (var i = 0; i < valid; i++) { sizeList[i] = ((depthList[i] - minD) * inv) | 0; counts[sizeList[i]]++; }
    var starts = new Uint32Array(65536);
    for (var i = 1; i < 65536; i++) starts[i] = starts[i-1] + counts[i-1];
    var result = new Uint32Array(valid);
    for (var i = 0; i < valid; i++) result[starts[sizeList[i]]++] = validIdx[i];
    self.postMessage({ indices: result }, [result.buffer]);
  }
};`;

function createSortWorker(): Worker {
  const blob = new Blob([workerSource], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
}

const MAX_CLOUDS = 16;

const vertexShader = /* glsl */ `
precision highp sampler2D;
precision highp usampler2D;
out vec4 vColor;
out vec3 vPosition;
uniform vec2 viewport;
uniform float focal;
uniform sampler2D centerAndScaleTexture;
uniform usampler2D covAndColorTexture;
uniform float numClouds;
uniform float cloudOffsets[${MAX_CLOUDS + 1}];
uniform mat4 cloudMV[${MAX_CLOUDS}];
in uint splatIndex;

vec2 unpackInt16(in uint value) {
  int v = int(value);
  int v0 = v >> 16;
  int v1 = (v & 0xFFFF);
  if((v & 0x8000) != 0) v1 |= 0xFFFF0000;
  return vec2(float(v1), float(v0));
}

void main () {
  int gIdx = int(splatIndex);
  int cloudId = 0;
  for (int c = 0; c < ${MAX_CLOUDS}; c++) {
    if (c >= int(numClouds)) break;
    if (float(gIdx) < cloudOffsets[c + 1]) { cloudId = c; break; }
  }
  mat4 mv = cloudMV[cloudId];
  ivec2 texSize = textureSize(centerAndScaleTexture, 0);
  ivec2 texPos = ivec2(gIdx % texSize.x, gIdx / texSize.x);
  vec4 centerAndScaleData = texelFetch(centerAndScaleTexture, texPos, 0);
  vec4 camspace = mv * vec4(centerAndScaleData.xyz, 1.0);
  vec4 pos2d = projectionMatrix * camspace;
  float bounds = 1.2 * pos2d.w;
  if (pos2d.z < -pos2d.w || pos2d.x < -bounds || pos2d.x > bounds || pos2d.y < -bounds || pos2d.y > bounds) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }
  uvec4 covAndColorData = texelFetch(covAndColorTexture, texPos, 0);
  vec2 c11c12 = unpackInt16(covAndColorData.x) * centerAndScaleData.w;
  vec2 c13c22 = unpackInt16(covAndColorData.y) * centerAndScaleData.w;
  vec2 c23c33 = unpackInt16(covAndColorData.z) * centerAndScaleData.w;
  mat3 Vrk = mat3(c11c12.x, c11c12.y, c13c22.x, c11c12.y, c13c22.y, c23c33.x, c13c22.x, c23c33.x, c23c33.y);
  mat3 J = mat3(focal / camspace.z, 0., -(focal * camspace.x) / (camspace.z * camspace.z), 0., focal / camspace.z, -(focal * camspace.y) / (camspace.z * camspace.z), 0., 0., 0.);
  mat3 T = transpose(mat3(mv)) * J;
  mat3 cov = transpose(T) * Vrk * T;
  vec2 vCenter = vec2(pos2d) / pos2d.w;
  float diagonal1 = cov[0][0] + 0.3;
  float offDiagonal = cov[0][1];
  float diagonal2 = cov[1][1] + 0.3;
  float mid = 0.5 * (diagonal1 + diagonal2);
  float radius = length(vec2((diagonal1 - diagonal2) / 2.0, offDiagonal));
  float lambda1 = mid + radius;
  float lambda2 = max(mid - radius, 0.1);
  vec2 diagonalVector = normalize(vec2(offDiagonal, lambda1 - diagonal1));
  vec2 v1 = min(sqrt(2.0 * lambda1), 1024.0) * diagonalVector;
  vec2 v2 = min(sqrt(2.0 * lambda2), 1024.0) * vec2(diagonalVector.y, -diagonalVector.x);
  uint colorUint = covAndColorData.w;
  vColor = vec4(float(colorUint & uint(0xFF)) / 255.0, float((colorUint >> uint(8)) & uint(0xFF)) / 255.0, float((colorUint >> uint(16)) & uint(0xFF)) / 255.0, float(colorUint >> uint(24)) / 255.0);
  vPosition = position;
  gl_Position = vec4(vCenter + position.x * v2 / viewport * 2.0 + position.y * v1 / viewport * 2.0, pos2d.z / pos2d.w, 1.0);
}`;

const fragmentShader = /* glsl */ `
in vec4 vColor;
in vec3 vPosition;
out vec4 fragColor;
uniform vec2 viewport;
uniform sampler2D cutoutMask;
uniform sampler2D cutoutDepth;
uniform bool hasCutoutMask;
uniform sampler2D priorityMask;
uniform bool hasPriorityMask;
uniform sampler2D screenMask;
uniform bool hasScreenMask;
void main () {
  float A = -dot(vPosition.xy, vPosition.xy);
  if (A < -4.0) discard;
  float B = exp(A) * vColor.a;
  if (B < 0.02) discard;
  vec2 screenUV = gl_FragCoord.xy / viewport;
  if (hasPriorityMask && texture(priorityMask, screenUV).r > 0.5) discard;
  if (hasCutoutMask && texture(cutoutMask, screenUV).r > 0.5) {
    float cutoutZ = texture(cutoutDepth, screenUV).r;
    if (gl_FragCoord.z >= cutoutZ) discard;
  }
  if (hasScreenMask && texture(screenMask, screenUV).r > 0.5) discard;
  fragColor = vec4(pow(vColor.rgb, vec3(1.05)), B);
}`;

export interface GlobalSplatManagerAPI {
  register(id: string, parsed: ParsedSplat): void;
  unregister(id: string): void;
  updateWorldMatrix(id: string, matrix: THREE.Matrix4): void;
  registerCutout(id: string, _object: THREE.Object3D, _material?: THREE.Material, _source?: THREE.Object3D, _mode?: SplatCutoutMode): void;
  unregisterCutout(id: string): void;
}

const GlobalSplatContext = createContext<GlobalSplatManagerAPI | null>(null);

interface CutoutEntry {
  object: THREE.Object3D;
  source: THREE.Object3D | null;
}

export type SplatCutoutMode = 'depth' | 'screen';

const maskMaterial = new THREE.MeshBasicMaterial({
  color: 0xffffff,
  side: THREE.DoubleSide,
});

export function useGlobalSplatManager(): GlobalSplatManagerAPI {
  const ctx = useContext(GlobalSplatContext);
  if (!ctx) throw new Error('useGlobalSplatManager must be used within GlobalSplatProvider');
  return ctx;
}

interface AtlasData {
  centerAndScaleTex: THREE.DataTexture;
  covAndColorTex: THREE.DataTexture;
  texWidth: number;
  texHeight: number;
  cloudOffsets: number[];
  cloudIds: string[];
}

function buildAtlas(registry: Map<string, CloudEntry>, maxTexSize: number): AtlasData | null {
  const cloudIds = Array.from(registry.keys()).slice(0, MAX_CLOUDS);
  if (cloudIds.length === 0) return null;
  let totalVertices = 0;
  const offsets: number[] = [];
  for (const id of cloudIds) {
    offsets.push(totalVertices);
    totalVertices += registry.get(id)!.parsed.numVertices;
  }
  offsets.push(totalVertices);

  const texWidth = Math.min(totalVertices, maxTexSize);
  const texHeight = Math.ceil(totalVertices / texWidth);
  const texels = texWidth * texHeight;
  const csData = new Float32Array(texels * 4);
  const ccData = new Uint32Array(texels * 4);
  let dstIdx = 0;
  for (const id of cloudIds) {
    const parsed = registry.get(id)!.parsed;
    for (let i = 0; i < parsed.numVertices; i++) {
      csData.set(parsed.centerAndScaleData.subarray(i * 4, i * 4 + 4), dstIdx * 4);
      ccData.set(parsed.covAndColorData.subarray(i * 4, i * 4 + 4), dstIdx * 4);
      dstIdx++;
    }
  }
  const centerTex = new THREE.DataTexture(csData, texWidth, texHeight, THREE.RGBAFormat, THREE.FloatType);
  centerTex.needsUpdate = true;
  const covTex = new THREE.DataTexture(ccData, texWidth, texHeight, THREE.RGBAIntegerFormat, THREE.UnsignedIntType);
  (covTex as THREE.DataTexture & { internalFormat?: string }).internalFormat = 'RGBA32UI';
  covTex.needsUpdate = true;
  return { centerAndScaleTex: centerTex, covAndColorTex: covTex, texWidth, texHeight, cloudOffsets: offsets, cloudIds };
}

function buildCombinedMatrices(registry: Map<string, CloudEntry>, cloudIds: string[]): Float32Array {
  let total = 0;
  for (const id of cloudIds) total += registry.get(id)!.parsed.numVertices;
  const combined = new Float32Array(total * 16);
  let offset = 0;
  for (const id of cloudIds) {
    const entry = registry.get(id)!;
    const src = entry.parsed.matrices;
    const wm = entry.worldMatrix.elements;
    for (let i = 0; i < entry.parsed.numVertices; i++) {
      const si = i * 16;
      const di = (offset + i) * 16;
      for (let j = 0; j < 16; j++) combined[di + j] = src[si + j];
      const lx = src[si + 12], ly = -src[si + 13], lz = src[si + 14];
      combined[di + 12] = wm[0] * lx + wm[4] * ly + wm[8] * lz + wm[12];
      combined[di + 13] = wm[1] * lx + wm[5] * ly + wm[9] * lz + wm[13];
      combined[di + 14] = wm[2] * lx + wm[6] * ly + wm[10] * lz + wm[14];
    }
    offset += entry.parsed.numVertices;
  }
  return combined;
}

function GlobalSplatMesh({
  registryRef,
  revisionRef,
  screenMaskTexture,
  maskScene,
  cutoutRegistryRef,
  priorityMaskScene,
  priorityRegistryRef,
}: {
  registryRef: React.RefObject<Map<string, CloudEntry>>;
  revisionRef: React.RefObject<number>;
  screenMaskTexture?: THREE.Texture | null;
  maskScene: THREE.Scene;
  cutoutRegistryRef: React.RefObject<Map<string, CutoutEntry>>;
  priorityMaskScene: THREE.Scene;
  priorityRegistryRef: React.RefObject<Map<string, CutoutEntry>>;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const geometryRef = useRef<THREE.InstancedBufferGeometry | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const readyRef = useRef(true);
  const lastRevRef = useRef(-1);
  const matrixRevRef = useRef(0);
  const lastMatrixRevRef = useRef(-1);
  const atlasRef = useRef<AtlasData | null>(null);
  const lastViewRef = useRef(new Float32Array(4));
  const sortPendingRef = useRef(false);
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);
  const size = useThree((s) => s.size);
  const invalidate = useThree((s) => s.invalidate);
  const maxTexSize = useMemo(() => gl.getContext().getParameter(gl.getContext().MAX_TEXTURE_SIZE) as number, [gl]);
  const maskRT = useMemo(() => {
    const rt = new THREE.WebGLRenderTarget(1, 1, {
      format: THREE.RedFormat,
      type: THREE.UnsignedByteType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
    });
    rt.depthTexture = new THREE.DepthTexture(1, 1);
    rt.depthTexture.format = THREE.DepthFormat;
    rt.depthTexture.type = THREE.UnsignedIntType;
    return rt;
  }, []);
  const priorityRT = useMemo(() => new THREE.WebGLRenderTarget(1, 1, {
    format: THREE.RedFormat,
    type: THREE.UnsignedByteType,
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    depthBuffer: true,
  }), []);

  useEffect(() => {
    const dpr = gl.getPixelRatio();
    const w = Math.round(size.width * dpr);
    const h = Math.round(size.height * dpr);
    maskRT.setSize(w, h);
    priorityRT.setSize(w, h);
    if (maskRT.depthTexture) {
      maskRT.depthTexture.image = { width: w, height: h };
      maskRT.depthTexture.needsUpdate = true;
    }
  }, [gl, maskRT, priorityRT, size.height, size.width]);

  const material = useMemo(() => {
    const cloudMVArray = Array.from({ length: MAX_CLOUDS }, () => new THREE.Matrix4());
    return new THREE.ShaderMaterial({
      uniforms: {
        viewport: { value: new THREE.Vector2(1920, 1080) },
        focal: { value: 1000 },
        centerAndScaleTexture: { value: null },
        covAndColorTexture: { value: null },
        numClouds: { value: 0 },
        cloudOffsets: { value: new Array(MAX_CLOUDS + 1).fill(0) },
        cloudMV: { value: cloudMVArray },
        cutoutMask: { value: maskRT.texture },
        cutoutDepth: { value: maskRT.depthTexture },
        hasCutoutMask: { value: false },
        priorityMask: { value: priorityRT.texture },
        hasPriorityMask: { value: false },
        screenMask: { value: null },
        hasScreenMask: { value: false },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      depthFunc: THREE.LessEqualDepth,
      blending: THREE.CustomBlending,
      blendSrcAlpha: THREE.OneFactor,
      toneMapped: false,
      glslVersion: THREE.GLSL3,
    });
  }, [maskRT.depthTexture, maskRT.texture, priorityRT.texture]);

  useEffect(() => {
    const worker = createSortWorker();
    workerRef.current = worker;
    const onMessage = (e: MessageEvent) => {
      readyRef.current = true;
      const geo = geometryRef.current;
      if (!geo) return;
      const indices = new Uint32Array(e.data.indices);
      const attr = geo.getAttribute('splatIndex') as THREE.InstancedBufferAttribute | undefined;
      if (attr && indices.length <= attr.count) {
        attr.set(indices);
        attr.needsUpdate = true;
      }
      geo.instanceCount = indices.length;
      invalidate();
    };
    worker.addEventListener('message', onMessage);
    return () => {
      worker.removeEventListener('message', onMessage);
      worker.terminate();
      workerRef.current = null;
    };
  }, [invalidate]);

  useEffect(() => () => {
    material.dispose();
    maskRT.dispose();
    priorityRT.dispose();
    geometryRef.current?.dispose();
    atlasRef.current?.centerAndScaleTex.dispose();
    atlasRef.current?.covAndColorTex.dispose();
  }, [maskRT, priorityRT, material]);

  const tempMV = useMemo(() => new THREE.Matrix4(), []);
  const tempClearColor = useMemo(() => new THREE.Color(), []);
  useFrame(() => {
    const worker = workerRef.current;
    const registry = registryRef.current;
    if (!worker || registry.size === 0) return;

    const atlasChanged = revisionRef.current !== lastRevRef.current;
    if (atlasChanged) {
      lastRevRef.current = revisionRef.current;
      atlasRef.current?.centerAndScaleTex.dispose();
      atlasRef.current?.covAndColorTex.dispose();
      const atlas = buildAtlas(registry, maxTexSize);
      atlasRef.current = atlas;
      if (!atlas) return;
      gl.initTexture(atlas.centerAndScaleTex);
      gl.initTexture(atlas.covAndColorTex);
      material.uniforms.centerAndScaleTexture.value = atlas.centerAndScaleTex;
      material.uniforms.covAndColorTexture.value = atlas.covAndColorTex;
      material.uniforms.numClouds.value = atlas.cloudIds.length;
      for (let i = 0; i <= atlas.cloudIds.length; i++) material.uniforms.cloudOffsets.value[i] = atlas.cloudOffsets[i];
      const geo = new THREE.InstancedBufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-2, -2, 0, 2, 2, 0, -2, 2, 0, -2, -2, 0, 2, -2, 0, 2, 2, 0]), 3));
      const idxAttr = new THREE.InstancedBufferAttribute(new Uint32Array(atlas.texWidth * atlas.texHeight), 1, false);
      idxAttr.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('splatIndex', idxAttr);
      geo.instanceCount = 0;
      geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10000);
      geometryRef.current?.dispose();
      geometryRef.current = geo;
      if (meshRef.current) meshRef.current.geometry = geo;
      matrixRevRef.current++;
      sortPendingRef.current = true;
    }

    const atlas = atlasRef.current;
    if (!atlas) return;
    let worldMatricesDirty = atlasChanged;
    for (const id of atlas.cloudIds) {
      const entry = registry.get(id);
      if (entry?.dirty) {
        entry.dirty = false;
        worldMatricesDirty = true;
      }
    }
    if (worldMatricesDirty) matrixRevRef.current++;
    if (matrixRevRef.current !== lastMatrixRevRef.current) {
      lastMatrixRevRef.current = matrixRevRef.current;
      const combined = buildCombinedMatrices(registry, atlas.cloudIds);
      const buf = combined.buffer.slice(0);
      worker.postMessage({ method: 'push', matrices: buf }, [buf]);
    }

    camera.updateMatrixWorld();
    for (let c = 0; c < atlas.cloudIds.length; c++) {
      const entry = registry.get(atlas.cloudIds[c]);
      if (entry) material.uniforms.cloudMV.value[c].copy(tempMV.multiplyMatrices(camera.matrixWorldInverse, entry.worldMatrix));
    }
    const vp = new THREE.Vector4();
    gl.getCurrentViewport(vp);
    material.uniforms.viewport.value.set(vp.z, vp.w);
    material.uniforms.focal.value = (vp.w / 2) * Math.abs(camera.projectionMatrix.elements[5]);
    material.uniforms.hasScreenMask.value = !!screenMaskTexture;
    material.uniforms.screenMask.value = screenMaskTexture ?? null;
    const hasCutouts = cutoutRegistryRef.current.size > 0;
    material.uniforms.hasCutoutMask.value = hasCutouts;
    const hasPriorityMasks = priorityRegistryRef.current.size > 0;
    material.uniforms.hasPriorityMask.value = hasPriorityMasks;
    if (hasCutouts || hasPriorityMasks) {
      const syncMaskEntries = (entries: Iterable<CutoutEntry>) => {
        for (const entry of entries) {
          if (entry.source) {
            entry.source.updateWorldMatrix(true, false);
            entry.object.matrix.copy(entry.source.matrixWorld);
            entry.object.matrixWorld.copy(entry.source.matrixWorld);
            entry.object.updateMatrixWorld(true);
          }
        }
      };
      syncMaskEntries(cutoutRegistryRef.current.values());
      syncMaskEntries(priorityRegistryRef.current.values());

      const renderMaskScene = (target: THREE.WebGLRenderTarget, scene: THREE.Scene) => {
        const prevRT = gl.getRenderTarget();
        const prevAutoClear = gl.autoClear;
        gl.getClearColor(tempClearColor);
        const prevClearAlpha = gl.getClearAlpha();
        gl.autoClear = false;
        gl.setRenderTarget(target);
        gl.setClearColor(0x000000, 0);
        gl.clear();
        gl.render(scene, camera);
        gl.setRenderTarget(prevRT);
        gl.setClearColor(tempClearColor, prevClearAlpha);
        gl.autoClear = prevAutoClear;
      };

      if (hasCutouts) renderMaskScene(maskRT, maskScene);
      if (hasPriorityMasks) renderMaskScene(priorityRT, priorityMaskScene);
    }

    if (!readyRef.current) return;
    const vm = camera.matrixWorldInverse.elements;
    const vx = vm[2], vy = vm[6], vz = vm[10], vw = vm[14];
    const lv = lastViewRef.current;
    const dot = lv[0] * vx + lv[1] * vy + lv[2] * vz;
    const lenSq = vx * vx + vy * vy + vz * vz;
    const lastLenSq = lv[0] * lv[0] + lv[1] * lv[1] + lv[2] * lv[2];
    const cosAngle = lenSq > 0 && lastLenSq > 0 ? dot / Math.sqrt(lenSq * lastLenSq) : 0;
    if (!sortPendingRef.current && !atlasChanged && !worldMatricesDirty && cosAngle > 0.9999 && Math.abs(vw - lv[3]) < 0.001) return;
    readyRef.current = false;
    sortPendingRef.current = false;
    lv[0] = vx; lv[1] = vy; lv[2] = vz; lv[3] = vw;
    const view = new Float32Array([vx, vy, vz, vw]);
    worker.postMessage({ method: 'sort', view: view.buffer }, [view.buffer]);
  });

  // Splats are transparent draws. Keep them before normal transparent mesh materials
  // so glass, decals, and alpha-blended product parts can composite over the splat background.
  return <mesh ref={meshRef} geometry={geometryRef.current ?? undefined} material={material} frustumCulled={false} renderOrder={-100} />;
}

export function GlobalSplatProvider({
  children,
  screenMaskTexture,
  screenMaskUrl,
}: {
  children: ReactNode;
  screenMaskTexture?: THREE.Texture | null;
  screenMaskUrl?: string | null;
}) {
  const registryRef = useRef<Map<string, CloudEntry>>(new Map());
  const revisionRef = useRef(0);
  const cutoutRegistryRef = useRef<Map<string, CutoutEntry>>(new Map());
  const priorityRegistryRef = useRef<Map<string, CutoutEntry>>(new Map());
  const invalidate = useThree((s) => s.invalidate);
  const maskScene = useMemo(() => new THREE.Scene(), []);
  const priorityMaskScene = useMemo(() => new THREE.Scene(), []);
  const [loadedScreenMask, setLoadedScreenMask] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    if (!screenMaskUrl) {
      setLoadedScreenMask(null);
      return;
    }
    let cancelled = false;
    const loader = new THREE.TextureLoader();
    loader.load(
      screenMaskUrl,
      (tex) => {
        if (cancelled) {
          tex.dispose();
          return;
        }
        tex.colorSpace = THREE.NoColorSpace;
        tex.flipY = false;
        setLoadedScreenMask(tex);
      },
      undefined,
      (err) => console.warn('[GlobalSplatProvider] screen mask failed to load', err),
    );
    return () => {
      cancelled = true;
    };
  }, [screenMaskUrl]);
  useEffect(() => () => loadedScreenMask?.dispose(), [loadedScreenMask]);
  const api = useMemo<GlobalSplatManagerAPI>(() => ({
    register(id, parsed) {
      registryRef.current.set(id, { parsed, worldMatrix: new THREE.Matrix4(), dirty: true });
      revisionRef.current++;
      invalidate();
    },
    unregister(id) {
      if (registryRef.current.delete(id)) {
        revisionRef.current++;
        invalidate();
      }
    },
    updateWorldMatrix(id, matrix) {
      const entry = registryRef.current.get(id);
      if (!entry || entry.worldMatrix.equals(matrix)) return;
      entry.worldMatrix.copy(matrix);
      entry.dirty = true;
      invalidate();
    },
    registerCutout(id, object, material, source, mode = 'depth') {
      const registry = mode === 'screen' ? priorityRegistryRef.current : cutoutRegistryRef.current;
      const scene = mode === 'screen' ? priorityMaskScene : maskScene;
      if (registry.has(id)) return;
      const mat = material ?? maskMaterial;
      object.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          (child as THREE.Mesh).material = mat;
        }
      });
      registry.set(id, { object, source: source ?? null });
      scene.add(object);
      invalidate();
    },
    unregisterCutout(id) {
      let changed = false;
      const depthEntry = cutoutRegistryRef.current.get(id);
      if (depthEntry) {
        maskScene.remove(depthEntry.object);
        cutoutRegistryRef.current.delete(id);
        changed = true;
      }
      const screenEntry = priorityRegistryRef.current.get(id);
      if (screenEntry) {
        priorityMaskScene.remove(screenEntry.object);
        priorityRegistryRef.current.delete(id);
        changed = true;
      }
      if (changed) invalidate();
    },
  }), [invalidate, maskScene, priorityMaskScene]);
  return (
    <GlobalSplatContext.Provider value={api}>
      {children}
      <GlobalSplatMesh
        registryRef={registryRef}
        revisionRef={revisionRef}
        screenMaskTexture={screenMaskTexture ?? loadedScreenMask}
        maskScene={maskScene}
        cutoutRegistryRef={cutoutRegistryRef}
        priorityMaskScene={priorityMaskScene}
        priorityRegistryRef={priorityRegistryRef}
      />
    </GlobalSplatContext.Provider>
  );
}

export function GsplatObject({
  id,
  url,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  visible = true,
  onLoaded,
}: {
  id: string;
  url: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  visible?: boolean;
  onLoaded?: () => void;
}) {
  const manager = useGlobalSplatManager();
  const groupRef = useRef<THREE.Group>(null);
  const [parsed, setParsed] = useState<ParsedSplat | null>(null);
  const src = useMemo(() => toViewSplatUrl(url), [url]);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setParsed(null);
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load splat: ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        setParsed(parseSplatBuffer(buffer));
        onLoadedRef.current?.();
      })
      .catch((err) => {
        if (!cancelled) console.warn('[GsplatObject]', err);
      });
    return () => { cancelled = true; };
  }, [src, visible]);

  useEffect(() => {
    if (!visible || !parsed) return;
    manager.register(id, parsed);
    return () => manager.unregister(id);
  }, [id, manager, parsed, visible]);

  useFrame(() => {
    if (!visible || !parsed || !groupRef.current) return;
    groupRef.current.updateWorldMatrix(true, false);
    manager.updateWorldMatrix(id, groupRef.current.matrixWorld);
  });

  return (
    <group
      ref={groupRef}
      position={position}
      rotation={rotation}
      scale={scale}
      visible={visible}
    />
  );
}

function createCutoutPrimitiveGeometry(shape: string, g: Record<string, unknown> = {}): THREE.BufferGeometry {
  switch (shape) {
    case 'box':
      return new THREE.BoxGeometry(
        Number(g.width ?? 1),
        Number(g.height ?? 1),
        Number(g.depth ?? 1),
        Number(g.widthSegments ?? 1),
        Number(g.heightSegments ?? 1),
        Number(g.depthSegments ?? 1),
      );
    case 'sphere':
      return new THREE.SphereGeometry(
        Number(g.radius ?? 0.5),
        Number(g.sphereWidthSegs ?? 32),
        Number(g.sphereHeightSegs ?? 32),
      );
    case 'cylinder':
      return new THREE.CylinderGeometry(
        Number(g.radiusTop ?? 0.5),
        Number(g.radiusBottom ?? 0.5),
        Number(g.cylinderHeight ?? 1),
        Number(g.radialSegments ?? 32),
        Number(g.cylinderHeightSegs ?? 1),
        Boolean(g.openEnded),
      );
    case 'plane':
      return new THREE.PlaneGeometry(
        Number(g.planeWidth ?? 1),
        Number(g.planeHeight ?? 1),
        Number(g.planeWidthSegs ?? 1),
        Number(g.planeHeightSegs ?? 1),
      );
    case 'cone':
      return new THREE.ConeGeometry(
        Number(g.coneRadius ?? 0.5),
        Number(g.coneHeight ?? 1),
        Number(g.coneRadialSegs ?? 32),
        Number(g.coneHeightSegs ?? 1),
        Boolean(g.coneOpenEnded),
      );
    case 'torus':
      return new THREE.TorusGeometry(
        Number(g.torusRadius ?? 0.4),
        Number(g.tube ?? 0.15),
        Number(g.torusRadialSegs ?? 16),
        Number(g.tubularSegments ?? 48),
        (Number(g.arc ?? 360) * Math.PI) / 180,
      );
    default:
      return new THREE.BoxGeometry(1, 1, 1);
  }
}

function useCutoutMaterial(cutoutTextureUrl?: string | null): THREE.Material | undefined {
  const [texture, setTexture] = useState<THREE.Texture | null>(null);
  useEffect(() => {
    if (!cutoutTextureUrl) {
      setTexture(null);
      return;
    }
    let cancelled = false;
    new THREE.TextureLoader().load(
      cutoutTextureUrl,
      (tex) => {
        if (cancelled) {
          tex.dispose();
          return;
        }
        tex.flipY = true;
        setTexture(tex);
      },
      undefined,
      (err) => console.warn('[SplatCutoutObject] cutout texture failed to load', err),
    );
    return () => {
      cancelled = true;
    };
  }, [cutoutTextureUrl]);
  useEffect(() => () => texture?.dispose(), [texture]);

  return useMemo(() => {
    if (!texture) return undefined;
    return new THREE.MeshBasicMaterial({
      color: 0xffffff,
      map: texture,
      alphaTest: 0.5,
      transparent: true,
      side: THREE.DoubleSide,
    });
  }, [texture]);
}

function useCutoutRegistration(id: string, object: THREE.Object3D | null, visible: boolean, material?: THREE.Material, mode: SplatCutoutMode = 'depth') {
  const manager = useGlobalSplatManager();
  const groupRef = useRef<THREE.Group>(null);
  useEffect(() => {
    if (!visible || !object) return;
    object.matrixAutoUpdate = false;
    manager.registerCutout(id, object, material, undefined, mode);
    return () => manager.unregisterCutout(id);
  }, [id, manager, material, mode, object, visible]);
  useFrame(() => {
    if (!visible || !object || !groupRef.current) return;
    groupRef.current.updateWorldMatrix(true, false);
    object.matrix.copy(groupRef.current.matrixWorld);
    object.matrixWorld.copy(groupRef.current.matrixWorld);
    object.matrixWorldNeedsUpdate = false;
  });
  return groupRef;
}

function PrimitiveCutout({
  id,
  primitiveShape,
  primitiveGeo,
  cutoutTextureUrl,
  position,
  rotation,
  scale,
  visible,
  mode,
}: SplatCutoutObjectProps & { visible: boolean }) {
  const material = useCutoutMaterial(cutoutTextureUrl);
  const maskMesh = useMemo(() => {
    const geo = createCutoutPrimitiveGeometry(primitiveShape || 'box', primitiveGeo);
    return new THREE.Mesh(geo, material ?? maskMaterial);
  }, [material, primitiveGeo, primitiveShape]);
  useEffect(() => () => {
    maskMesh.geometry.dispose();
  }, [maskMesh]);
  const groupRef = useCutoutRegistration(id, maskMesh, visible, material, mode);
  return <group ref={groupRef} position={position} rotation={rotation} scale={scale} visible={false} />;
}

function MeshCutout({
  id,
  url,
  cutoutTextureUrl,
  position,
  rotation,
  scale,
  visible,
  mode,
}: SplatCutoutObjectProps & { visible: boolean }) {
  const gltf = useGLTF(url || '');
  const material = useCutoutMaterial(cutoutTextureUrl);
  const maskObject = useMemo(() => {
    const clone = gltf.scene.clone(true);
    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        (child as THREE.Mesh).material = material ?? maskMaterial;
      }
    });
    return clone;
  }, [gltf.scene, material]);
  const groupRef = useCutoutRegistration(id, maskObject, visible, material, mode);
  return <group ref={groupRef} position={position} rotation={rotation} scale={scale} visible={false} />;
}

export interface SplatCutoutObjectProps {
  id: string;
  type: 'mesh' | 'primitive';
  url?: string | null;
  primitiveShape?: string;
  primitiveGeo?: Record<string, unknown>;
  cutoutTextureUrl?: string | null;
  mode?: SplatCutoutMode;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  visible?: boolean;
}

export function SplatCutoutObject(props: SplatCutoutObjectProps) {
  const visible = props.visible !== false;
  if (!visible) return null;
  if (props.type === 'primitive') return <PrimitiveCutout {...props} visible={visible} />;
  if (!props.url || !/\.(glb|gltf)(\?|$)/i.test(props.url)) return null;
  return <MeshCutout {...props} visible={visible} />;
}
